import api from '../utils/apiClient';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api.config';

/**
 * Generate a trial code with 16 characters (mix of letters and numbers)
 * This code is formatted as 4 groups of 4 characters (XXXX-XXXX-XXXX-XXXX)
 * for easier input in the school code fields
 * @returns {string} The generated trial code
 */
export const generateTrialCode = () => {
  // Note: This is only kept in the frontend for compatibility
  // Actual code generation should happen on the backend
  return api.get('/api/v1/stem@home/trial/generate-code')
    .then(response => response.data.code)
    .catch(error => {
      console.error('Error generating trial code:', error);
      // Fallback to a placeholder if the API fails
      return 'XXXX-XXXX-XXXX-XXXX';
    });
};

/**
 * Get user data from localStorage
 * @returns {Object|null} User data object or null if not found
 */
const getUser = () => {
  const userData = localStorage.getItem('user');
  if (!userData) return null;
  try {
    return JSON.parse(userData);
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Service for handling trial-related API interactions
 */
class TrialService {
  /**
   * Create a new trial account
   * @param {Object} data - Trial creation data including email, name, and phoneNumber
   * @returns {Promise} - API response with trial code and details
   */
  createTrial = async (data) => {
    try {
      const response = await api.post('/api/v1/stem@home/trial/create', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Validate a trial code
   * @param {string} code - The trial code to validate
   * @returns {Promise} - API response with trial status and details
   */
  validateTrialCode = async (code) => {
    try {
      const response = await api.get(`/api/v1/stem@home/trial/validate/${code}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Get the current trial status for the logged-in user
   * @returns {Promise} - API response with trial status and limits
   */
  getTrialStatus = async () => {
    try {
      const response = await api.get('/api/v1/stem@home/trial/status', { 
        headers: getAuthHeaders() 
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Convert a trial account to a paid account
   * @param {string} planId - The ID of the plan to upgrade to
   * @returns {Promise} - API response with upgrade status
   */
  upgradeTrial = async (planId) => {
    try {
      const response = await api.post('/api/v1/stem@home/trial/upgrade', 
        { planId },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };
}

export default new TrialService(); 